// Admin › Settings › Referral Program — the rewards program terms section.
import type { TermsField, TermsGroupId } from '../../api/rewards'
import type { LocalizedDict } from '../index'

export type RewardsTermsStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  status: { on: string; off: string }
  versionLine: (version: number, date: string, who: string | null) => string
  switch: {
    turnOn: string
    turnOff: string
    onTitle: string
    offTitle: string
    onBody: string
    offBody: string
    confirmOn: string
    confirmOff: string
    cancel: string
  }
  groups: Record<TermsGroupId, string>
  fields: Record<TermsField, string>
  units: { usd: string; days: string; months: string; orders: string; referrals: string; percent: string }
  percentValue: (n: number) => string
  invalid: string
  save: string
  reset: string
  saveDialog: {
    title: string
    body: string
    changes: string
    noteLabel: string
    notePlaceholder: string
    noteRequired: string
    cancel: string
    confirm: string
    saving: string
  }
  saved: (version: number) => string
  unchanged: string
  trial: {
    title: string
    hint: string
    label: string
    save: string
    saved: string
  }
  history: {
    title: (count: number) => string
    first: string
    noChanges: string
  }
}

export const rewardsTermsI18n: LocalizedDict<RewardsTermsStrings> = {
  en: {
    title: 'Rewards program',
    subtitle: 'Entry fee, credit, referral rewards and commission. A change applies to new members only.',
    loading: 'Loading…',
    loadError: "Couldn't load the rewards terms.",
    retry: 'Try again',
    status: { on: 'On', off: 'Off' },
    versionLine: (version, date, who) =>
      who ? `Version ${version} · ${date} · ${who}` : `Version ${version} · ${date}`,
    switch: {
      turnOn: 'Turn on',
      turnOff: 'Turn off',
      onTitle: 'Turn the rewards program on?',
      offTitle: 'Turn the rewards program off?',
      onBody:
        'New referral codes will join the current terms. The entry fee and rewards start working as their parts are released.',
      offBody:
        'New referral codes will no longer join the rewards terms. Members who already joined keep their terms.',
      confirmOn: 'Turn on',
      confirmOff: 'Turn off',
      cancel: 'Cancel',
    },
    groups: {
      entry: 'Joining',
      qualify: 'When a referral counts',
      rewards: 'Referral rewards',
      commission: 'Commission',
    },
    fields: {
      entry_fee_usd: 'Entry fee',
      entry_credit_usd: 'Credit at joining',
      entry_trial_days: 'Subscription trial',
      qualify_min_days: 'Days after paying',
      qualify_min_shipped_orders: 'Shipped orders',
      threshold_count: 'Referrals for the big reward',
      threshold_reward_usd: 'Big reward',
      per_referral_reward_usd: 'Each referral after that',
      reward_credit_expiry_months: 'Reward credit expires after',
      commission_percent: 'Share of referee sales',
      commission_period_days: 'Share paid for',
      conversion_bonus_percent: 'Bonus when converted to credit',
    },
    units: {
      usd: 'USD',
      days: 'days',
      months: 'months',
      orders: 'orders',
      referrals: 'referrals',
      percent: '%',
    },
    percentValue: (n) => `${n}%`,
    invalid: 'Enter a valid number',
    save: 'Save as new version',
    reset: 'Undo changes',
    saveDialog: {
      title: 'Save a new version?',
      body: 'Members who already joined keep the version they joined under. New members get this one.',
      changes: 'Changes',
      noteLabel: 'Why are you changing this?',
      notePlaceholder: 'e.g. Higher reward for the holiday season',
      noteRequired: 'Write at least 3 characters.',
      cancel: 'Cancel',
      confirm: 'Save version',
      saving: 'Saving…',
    },
    saved: (version) => `Saved as version ${version}.`,
    unchanged: 'Nothing changed since the current version.',
    trial: {
      title: 'Trial without the entry fee',
      hint: 'Free subscription days for sellers who did not pay the entry fee.',
      label: 'Trial',
      save: 'Save',
      saved: 'Saved.',
    },
    history: {
      title: (count) => `Version history (${count})`,
      first: 'Launch values',
      noChanges: 'No number changed',
    },
  },
  tr: {
    title: 'Ödül programı',
    subtitle: 'Giriş ücreti, kredi, referans ödülleri ve yüzde pay. Değişiklik yalnız yeni üyelere uygulanır.',
    loading: 'Yükleniyor…',
    loadError: 'Ödül şartları yüklenemedi.',
    retry: 'Tekrar dene',
    status: { on: 'Açık', off: 'Kapalı' },
    versionLine: (version, date, who) =>
      who ? `Sürüm ${version} · ${date} · ${who}` : `Sürüm ${version} · ${date}`,
    switch: {
      turnOn: 'Programı aç',
      turnOff: 'Programı kapat',
      onTitle: 'Ödül programı açılsın mı?',
      offTitle: 'Ödül programı kapatılsın mı?',
      onBody:
        'Yeni referans kodları güncel şartlara bağlanır. Giriş ücreti ve ödüller, ilgili parçalar yayına çıktıkça çalışmaya başlar.',
      offBody:
        'Yeni referans kodları artık ödül şartlarına bağlanmaz. Daha önce katılan üyeler kendi şartlarıyla devam eder.',
      confirmOn: 'Aç',
      confirmOff: 'Kapat',
      cancel: 'Vazgeç',
    },
    groups: {
      entry: 'Katılım',
      qualify: 'Referans ne zaman sayılır',
      rewards: 'Referans ödülleri',
      commission: 'Yüzde pay',
    },
    fields: {
      entry_fee_usd: 'Giriş ücreti',
      entry_credit_usd: 'Girişte verilen kredi',
      entry_trial_days: 'Abonelik denemesi',
      qualify_min_days: 'Ödemeden sonra geçen gün',
      qualify_min_shipped_orders: 'Gönderilmiş sipariş',
      threshold_count: 'Büyük ödül için referans',
      threshold_reward_usd: 'Büyük ödül',
      per_referral_reward_usd: 'Sonraki her referans',
      reward_credit_expiry_months: 'Ödül kredisinin süresi',
      commission_percent: 'Getirilenin satışından pay',
      commission_period_days: 'Pay süresi',
      conversion_bonus_percent: 'Krediye çevirme bonusu',
    },
    units: {
      usd: 'USD',
      days: 'gün',
      months: 'ay',
      orders: 'sipariş',
      referrals: 'referans',
      percent: '%',
    },
    percentValue: (n) => `%${n}`,
    invalid: 'Geçerli bir sayı gir',
    save: 'Yeni sürüm olarak kaydet',
    reset: 'Değişiklikleri geri al',
    saveDialog: {
      title: 'Yeni sürüm kaydedilsin mi?',
      body: 'Daha önce katılan üyeler katıldıkları sürümle devam eder. Yeni üyeler bu sürümü alır.',
      changes: 'Değişenler',
      noteLabel: 'Neden değiştiriyorsun?',
      notePlaceholder: 'ör. Yılbaşı için daha yüksek ödül',
      noteRequired: 'En az 3 karakter yaz.',
      cancel: 'Vazgeç',
      confirm: 'Sürümü kaydet',
      saving: 'Kaydediliyor…',
    },
    saved: (version) => `Sürüm ${version} olarak kaydedildi.`,
    unchanged: 'Güncel sürümden farklı bir değer yok.',
    trial: {
      title: 'Giriş ücreti ödemeyenlerin denemesi',
      hint: 'Giriş ücreti ödemeyen satıcılar için ücretsiz abonelik günü.',
      label: 'Deneme',
      save: 'Kaydet',
      saved: 'Kaydedildi.',
    },
    history: {
      title: (count) => `Sürüm geçmişi (${count})`,
      first: 'Açılış değerleri',
      noChanges: 'Hiçbir rakam değişmedi',
    },
  },
}
